import { useState } from "react"

import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { FaSync } from "react-icons/fa"

import { AuditPoolReport, AuditPoolReportGenerate } from "../../../api/Reports/WorkforceReport"
import FormatDate from "../../../formatters/FormatDate"
import handleAPIError from "../../../utils/handleAPIError"
import printPdf from "../../../utils/printPdf"
import ActionButton from "../../ActionButton"
import FromToDates from "../../Reports/FromToDates"
import FormatTimestampFileName from "../../../formatters/FormatTimestampFileName"
import exportCsv from "../../../utils/exportCsv"
import PoolHistoryDetailsParameters from "../../../api/Randoms/RandomsModel/PoolHistoryDetailsParameters"
import PoolHistoryDetailsGet from "../../../api/Randoms/PoolHistoryDetailsGet"

interface AuditPoolModalProps {
  poolID: number
  poolName: string
  reportType: string
  onClose: () => void
}

const AuditPoolModal = (props: AuditPoolModalProps) => {
  const { onClose, poolID, poolName, reportType } = props
  const [generating, setGenerating] = useState(false)
  const [from, setFrom] = useState<Date>()
  const [to, setTo] = useState<Date>()
  const [includeAllDates, setIncludeAllDates] = useState<boolean>(false)
  const [pdfOutput, setPdfOutput] = useState(false)

  const handleCancelClick = () => onClose()
  const handleClearClick = () => {
    setFrom(undefined)
    setTo(undefined)
  }

  const checkAllDates = (checked: boolean) => {
    setIncludeAllDates(checked)
    setFrom(undefined)
    setTo(undefined)
  }

  const exportAuditDetails = () => {
    const params: AuditPoolReport = {
      DateFrom: FormatDate(from),
      DateTo: FormatDate(to),
      AllDates: includeAllDates,
      PoolID: poolID,
      PoolName: poolName,
      GeneratePDF: pdfOutput,
    }

    AuditPoolReportGenerate(params).subscribe(
      (data: string) => {
        setGenerating(false)

        if (pdfOutput) {
          printPdf(data)
        } else {
          exportCsv(
            data,
            FormatTimestampFileName("Audit_Pool_Report", "csv", new Date()),
          )
        }
      },
      (error) => handleAPIError(error, () => setGenerating(false)),
    )
  }

  const exportHistoryDetails = () => {
    const params: PoolHistoryDetailsParameters = {
      DateFrom: FormatDate(from),
      DateTo: FormatDate(to),
      AllDates: includeAllDates,
      ImplementationID: 0,
      PoolID: poolID,
      PoolName: poolName,
      PoolProgramID: 0,
      PoolSelectionID: 0,
      GeneratePDF: pdfOutput,
    }

    PoolHistoryDetailsGet(params).subscribe(
      (data: string) => {
        setGenerating(false)

        if (pdfOutput) {
          printPdf(data)
        } else {
          exportCsv(
            data,
            FormatTimestampFileName("Pool_History_Details", "csv", new Date()),
          )
        }
      },
      (error) => handleAPIError(error, () => setGenerating(false)),
    )
  }

  const handleGenerateCsvClick = () => {
    setGenerating(true)

    switch (reportType) {
      case "audit":
        exportAuditDetails()
        break
      case "history":
        exportHistoryDetails()
        break
      default:
        return
    }
  }

  const isParamsValid = () => includeAllDates || (!!from && !!to)

  return (
    <Modal backdrop="static" centered show onHide={() => handleCancelClick()}>
      <Modal.Header closeButton>
        <Modal.Title className="text-uppercase">EXPORT CSV OPTIONS:</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3">
        <Form>
          <Row>
            <Col sm={4}>
              <Form.Label htmlFor="include_all_dates">Include All Dates</Form.Label>
            </Col>
            <Col>
              <Form.Check
                id="include_all_dates"
                type="checkbox"
                inline
                className="abc-checkbox abc-checkbox-inline"
                label=""
                value={includeAllDates ? 1 : 0}
                checked={includeAllDates}
                onChange={(e) => checkAllDates(e.target.checked)}
              />
            </Col>
          </Row>
          {!includeAllDates && (
            <Row>
              <Col>
                <FromToDates from={from} to={to} onChangeFrom={setFrom} onChangeTo={setTo} />
              </Col>
            </Row>
          )}
          <Row>
            <Col sm={4}>
              <Form.Label htmlFor="pdf_output">Generate PDF</Form.Label>
            </Col>
            <Col>
              <Form.Check
                id="pdf_output"
                type="checkbox"
                inline
                className="abc-checkbox abc-checkbox-inline"
                label=""
                value={pdfOutput ? 1 : 0}
                checked={pdfOutput}
                onChange={(e) => setPdfOutput(e.target.checked)}
              />
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancelClick}>
          Cancel
        </Button>
        <Button variant="secondary" className="mr-auto" onClick={handleClearClick}>
          Clear Filter&nbsp;
          <FaSync />
        </Button>
        <ActionButton
          variant="primary"
          disabled={!isParamsValid()}
          onClick={handleGenerateCsvClick}
          spin={generating}
          disableOnSpin
        >
          Export
        </ActionButton>
      </Modal.Footer>
    </Modal>
  )
}
export default AuditPoolModal
